// A flagged session in miniature, under its Triage row: the trace page's
// compact card of the stretch of the record around the flagged messages, the
// flagged turns pinned. One read, the card's code loading beside it. A click
// previews the session's workspace (previewTraceCanvas).
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { readConversationPage } from "../../lib/conversationRead.js";
import { findingStory } from "../../lib/findingStory.js";
import { previewTraceCanvas } from "../../lib/traceCanvasStore.js";
import { recordWindow } from "../../lib/triageTimeline.js";
import FindingStory, { StoryLoading, storyPins } from "./FindingStory.jsx";
import { machineOf } from "./reviewSessions.jsx";

const cardModule = () => import("../WorkspaceCardPreview.jsx");
const WorkspaceCardPreview = lazy(cardModule);
// Enough on either side of the flagged message to see what led up to it.
const AROUND = 120;
const FOLLOWING = 60;

// The page around a flagged message, read once per page load. A read costs
// about the same whatever its size, so it starts when the pointer reaches the
// row (prefetchSessionPage) and the opened row usually finds it done.
const pages = new Map();
// A harness or a test answers for the page: (session, messageId) -> turns.
let seeded = null;
export function seedSessionPages(answer) {
  seeded = typeof answer === "function" ? answer : null;
  pages.clear();
}
function pageAround(session, messageId) {
  const key = `${session}:${String(messageId).toLowerCase()}`;
  if (!pages.has(key) && seeded) pages.set(key, Promise.resolve(seeded(session, messageId)));
  if (!pages.has(key)) {
    pages.set(key, readConversationPage({ traceId: session, timeRange: recordWindow(), aroundMessageId: messageId, maxMessages: AROUND, following: FOLLOWING })
      // A server that predates the field serves the newest page: not this one.
      .then((read) => (read?.aroundHonoured ? read.turns || [] : null))
      .catch(() => { pages.delete(key); return null; }));
  }
  return pages.get(key);
}
export function prefetchSessionPage(session, findings) {
  const first = findings.flatMap((f) => f.messageIds || [])[0];
  if (session && first) pageAround(session, first);
  cardModule();
}
export function _clearSessionPagesForTests() { pages.clear(); }

// Each finding's turns in these turns: the messages its review cited (and its
// verified quotes', where they carry one).
export function flaggedTurns(findings, turns) {
  const at = new Map();
  (turns || []).forEach((t, i) => { const id = String(t?.messageId || "").toLowerCase(); if (id && !at.has(id)) at.set(id, i); });
  return findings.map((f) => [...new Set((f.messageIds || []).map((id) => at.get(String(id).toLowerCase())).filter((i) => i != null))].sort((a, b) => a - b));
}

function usePageAround(session, messageId) {
  const [page, setPage] = useState(undefined);
  useEffect(() => {
    if (!session || !messageId) { setPage(null); return undefined; }
    let live = true;
    setPage(undefined);
    pageAround(session, messageId).then((turns) => { if (live) setPage(turns); });
    return () => { live = false; };
  }, [session, messageId]);
  return page;
}

const NOTE = "text-[11px] leading-[1.4] text-[color:var(--muted)]";

// findings: [{ messageIds, title, tone?, met?, quotes? }], numbered as the list
// numbers them; highlight: the index of the one pointed at, its pins red and
// the rest muted, unless a finding brings its own tone. `around`: the message
// the page centres on instead of the first flagged one. `notes`: more pins,
// in the same shape. `story`: when one of the findings met a standard, what
// happened around them is drawn on the card (FindingStory): its moments are
// the card's pins, and a click on the card picks one rather than opening the
// session. `told(indexes, story)`: what the host says about those findings, in the
// panel under the card when a flagged message they cite is picked;
// `toldSummary(story)`: what happened, once above them all;
// `onTold(bool | null)` says whether the story is drawn, null until the page
// is read, for the host to leave its own list of them off meanwhile.
export default function TriageSessionPreview({ session, summary = null, endpointId = null, findings, highlight = null, around = null, notes = null, story = false, told: aboutFindings = null, toldSummary = null, onTold = null }) {
  const firstFlagged = around || findings.flatMap((f) => f.messageIds || [])[0] || null;
  const turns = usePageAround(session, firstFlagged);
  // The page starts mid-session, so its first message is no name for it: the
  // session's own label, else what the row calls it.
  // The person registered to the machine rides on its head, as the row's
  // session column, which the card stands in for when open, names them.
  const registeredUser = machineOf(endpointId)?.user || null;
  const trace = useMemo(() => ({ id: session, sessionId: session, summary: summary || `Session ${String(session).slice(0, 8)}`, endpointId, registeredUser }), [session, summary, endpointId, registeredUser]);
  const told = useMemo(() => (story && turns?.length ? findingStory(turns, findings) : null), [story, turns, findings]);
  const telling = !!told?.met;
  const decided = turns !== undefined;
  useEffect(() => { onTold?.(decided ? telling : null); }, [decided, telling, onTold]);
  const [anchors, setAnchors] = useState(null);
  // The pin of the moment picked in the story, which the card rings.
  const [ringed, setRinged] = useState(null);
  // The card opens zoomed into the story's stretch, a message either side.
  const lens = useMemo(() => {
    if (!telling) return null;
    const at = (i) => turns[Math.max(0, Math.min(turns.length - 1, i))]?.messageAt;
    const t0 = at(told.from - 1) ?? at(told.from);
    const t1 = at(told.to + 1) ?? at(told.to);
    return Number.isFinite(t0) && Number.isFinite(t1) && t1 > t0 ? { t0, t1 } : null;
  }, [telling, told, turns]);
  const pins = useMemo(() => (telling
    ? storyPins(told, ringed)
    : [
      ...flaggedTurns(findings, turns).flatMap((ts, i) => ts.map((turn) => ({
        turn, tone: findings[i].tone || (highlight == null || highlight === i ? "danger" : "calm"), count: i + 1, title: `${i + 1}. ${findings[i].title || "Flagged"}`,
      }))),
      ...(notes?.length ? flaggedTurns(notes, turns).flatMap((ts, i) => ts.map((turn) => ({ turn, tone: notes[i].tone || "calm", count: i + 1, title: notes[i].title || "Note" }))) : []),
    ]), [telling, told, ringed, findings, notes, turns, highlight]);
  const openAt = (flag) => {
    previewTraceCanvas({ id: session, title: summary || "", message: flag.messageId });
  };
  // A story loads behind the same dots its card waits behind, so the two
  // waits read as one.
  const loading = story
    ? <span data-lite-preview="loading"><StoryLoading /></span>
    : <span data-lite-preview="loading" aria-busy="true" className={NOTE}>Loading the session{"\u2026"}</span>;
  if (turns === undefined) return loading;
  if (!turns?.length) return <span data-lite-preview="error" className={NOTE}>Couldn&rsquo;t load this session&rsquo;s trace.</span>;
  return (
    <div data-lite-preview="card" className="min-w-0">
      <Suspense fallback={loading}>
        {telling ? (
          <FindingStory story={told} turns={turns} anchors={anchors} findingsFor={aboutFindings} summaryFor={toldSummary} onOpen={openAt} onRing={setRinged}>
            <WorkspaceCardPreview trace={trace} turns={turns} pins={pins} review={false} onAnchors={setAnchors} lens={lens} onlyPinned />
          </FindingStory>
        ) : (
          <WorkspaceCardPreview trace={trace} turns={turns} pins={pins} review={false} onOpen={() => previewTraceCanvas({ id: session, title: summary || "" })} />
        )}
      </Suspense>
    </div>
  );
}
